import { loginSchema, magicLinkRequestSchema, setupSchema } from '@helpdesk/shared';
import { useMutation } from '@tanstack/react-query';
import { KeyRound, MailCheck } from 'lucide-react';
import { useEffect, useRef, useState, type FormEvent, type InputHTMLAttributes, type ReactNode } from 'react';
import { Link, Navigate, useNavigate, useSearchParams } from 'react-router';
import { BackButton } from '../components/ui/back.tsx';
import { Button, ButtonLink } from '../components/ui/button.tsx';
import { Field, Input } from '../components/ui/form.tsx';
import { Alert, Card, Spinner } from '../components/ui/misc.tsx';
import { useToast } from '../components/ui/toast.tsx';
import { api, ApiError } from '../lib/api.ts';
import { usePageTitle } from '../lib/navigation.ts';
import { loadReporter, saveReporter } from '../lib/report.ts';
import { useSession, useSetSessionUser } from '../lib/session.ts';
import type { ConfirmedTicket, User } from '../lib/types.ts';
import { useHashToken } from '../lib/useHashToken.ts';
import { formValues, validate, type FieldErrors } from '../lib/validate.ts';

/** Where to go after the emailed link is opened (possibly in a new tab). */
const NEXT_KEY = 'helpdesk.next';

/**
 * Only in-app paths are allowed as a destination after sign-in: "//host"
 * or "/\host" would otherwise send people to another site.
 */
export const safePath = (value: string | null | undefined): string | null =>
  value && value.startsWith('/') && !value.startsWith('//') && !value.startsWith('/\\') ? value : null;

function rememberNext(next: string | null) {
  try {
    if (safePath(next)) localStorage.setItem(NEXT_KEY, next!);
    else localStorage.removeItem(NEXT_KEY);
  } catch {
    // Storage unavailable: we'll land on the home page instead.
  }
}

function takeNext(): string {
  try {
    const next = localStorage.getItem(NEXT_KEY);
    localStorage.removeItem(NEXT_KEY);
    return safePath(next) ?? '/';
  } catch {
    return '/';
  }
}

function AuthCard({
  title,
  subtitle,
  top,
  back,
  children,
}: {
  title: string;
  subtitle?: ReactNode;
  top?: ReactNode;
  /** Where Back goes when the visit started here; no Back button without it. */
  back?: string;
  children: ReactNode;
}) {
  usePageTitle(title);
  return (
    <div className="mx-auto max-w-md pt-4 sm:pt-10">
      {back && (
        <div className="mb-2">
          <BackButton to={back} />
        </div>
      )}
      <Card className="p-6 sm:p-8">
        {top && <div className="mb-6">{top}</div>}
        <h1 className="text-xl font-bold tracking-tight">{title}</h1>
        {subtitle && <p className="mt-1.5 text-muted">{subtitle}</p>}
        <div className="mt-6">{children}</div>
      </Card>
    </div>
  );
}

// ── Sign in ─────────────────────────────────────────────────────────

type SignInMode = 'email' | 'password';

/** The last tab used on this device, so IT phones reopen on the password form. */
const MODE_KEY = 'helpdesk.signInMode';

function rememberedMode(): SignInMode {
  try {
    return localStorage.getItem(MODE_KEY) === 'password' ? 'password' : 'email';
  } catch {
    return 'email';
  }
}

export function SignInPage() {
  const [params] = useSearchParams();
  const { user, setupRequired } = useSession();
  const [mode, setModeState] = useState<SignInMode>(() => (params.get('it') ? 'password' : rememberedMode()));
  const next = params.get('next');

  if (user) return <Navigate to={safePath(next) ?? '/'} replace />;
  if (setupRequired) return <Navigate to="/setup" replace />;

  const setMode = (value: SignInMode) => {
    setModeState(value);
    try {
      localStorage.setItem(MODE_KEY, value);
    } catch {
      // Not remembered in private browsing.
    }
  };
  const tabs = <ModeTabs mode={mode} onChange={setMode} />;

  return mode === 'email' ? (
    <MagicLinkForm next={next} tabs={tabs} />
  ) : (
    <PasswordForm next={next} tabs={tabs} />
  );
}

function ModeTabs({ mode, onChange }: { mode: SignInMode; onChange: (mode: SignInMode) => void }) {
  const tab = (value: SignInMode, label: string) => (
    <button
      type="button"
      role="tab"
      aria-selected={mode === value}
      onClick={() => onChange(value)}
      className={`flex-1 rounded-md px-3 py-2 text-sm font-medium ${
        mode === value ? 'bg-surface text-text shadow-card' : 'text-muted hover:text-text'
      }`}
    >
      {label}
    </button>
  );
  return (
    <div role="tablist" aria-label="Who is signing in" className="flex rounded-lg bg-surface-2 p-1">
      {tab('email', 'Staff')}
      {tab('password', 'IT staff')}
    </div>
  );
}

function MagicLinkForm({ next, tabs }: { next: string | null; tabs: ReactNode }) {
  const [errors, setErrors] = useState<FieldErrors>({});
  const [sentTo, setSentTo] = useState<string | null>(null);
  // The address this device last used (to sign in or on a report).
  const [rememberedEmail] = useState(() => loadReporter().email ?? '');
  const send = useMutation({
    mutationFn: (email: string) => api.post('/api/auth/magic-link', { email }),
    onSuccess: (_, email) => {
      saveReporter({ email });
      setSentTo(email);
    },
    onError: (err) =>
      setErrors(err instanceof ApiError ? { ...err.fields, _: err.message } : { _: String(err) }),
  });

  const submit = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const result = validate(magicLinkRequestSchema, formValues(e.currentTarget));
    if (!result.ok) return setErrors(result.fields);
    setErrors({});
    rememberNext(next);
    send.mutate(result.data.email);
  };

  if (sentTo) {
    return (
      <EmailCodeStep
        email={sentTo}
        onResend={() => send.mutateAsync(sentTo)}
        onDifferentEmail={() => setSentTo(null)}
      />
    );
  }

  return (
    <AuthCard
      title="Sign in"
      subtitle="Follow your tickets, reply to IT and get notified about updates."
      top={tabs}
      back="/"
    >
      <form onSubmit={submit} noValidate className="space-y-4">
        {errors._ && <Alert tone="error">{errors._}</Alert>}
        <Field
          label="Work email"
          error={errors.email}
          hint="We’ll email you a link and a code. No password needed."
        >
          <Input
            name="email"
            type="email"
            autoComplete="email"
            inputMode="email"
            defaultValue={rememberedEmail}
            autoFocus={!rememberedEmail}
          />
        </Field>
        <Button type="submit" className="w-full" loading={send.isPending}>
          Email me a sign-in link
        </Button>
      </form>
      <p className="mt-6 border-t border-line pt-5 text-center text-sm">
        <Link to="/new" className="font-medium text-brand hover:underline">
          Report a problem without signing in
        </Link>
      </p>
    </AuthCard>
  );
}

const RESEND_SECONDS = 30;

/**
 * After asking for a link: tap the link in the email, or type its 6-digit
 * code here. The code matters on iPhone, where the installed app keeps its
 * own sign-in and a link from the email opens in Safari instead.
 */
function EmailCodeStep({
  email,
  onResend,
  onDifferentEmail,
}: {
  email: string;
  onResend: () => Promise<unknown>;
  onDifferentEmail: () => void;
}) {
  const setUser = useSetSessionUser();
  const navigate = useNavigate();
  const [code, setCode] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [wait, setWait] = useState(RESEND_SECONDS);
  const [resent, setResent] = useState(false);

  useEffect(() => {
    if (wait <= 0) return;
    const timer = setTimeout(() => setWait((w) => w - 1), 1000);
    return () => clearTimeout(timer);
  }, [wait]);

  const verify = useMutation({
    mutationFn: (value: string) =>
      api.post<{ user: User }>('/api/auth/magic-link/code', { email, code: value }),
    onSuccess: ({ user }) => {
      setUser(user);
      navigate(takeNext(), { replace: true });
    },
    onError: (err) => {
      setError(err instanceof ApiError ? err.message : String(err));
      setCode('');
    },
  });

  const change = (value: string) => {
    const digits = value.replace(/\D/g, '').slice(0, 6);
    setCode(digits);
    setError(null);
    // Six digits is the whole code: no need to find a button.
    if (digits.length === 6 && !verify.isPending) verify.mutate(digits);
  };

  return (
    <AuthCard title="Check your email">
      <div className="flex flex-col items-center text-center">
        <span className="mb-4 flex size-14 items-center justify-center rounded-full bg-brand-soft text-brand">
          <MailCheck className="size-7" aria-hidden />
        </span>
        <p>
          We’ve sent a sign-in link and code to <strong className="break-all">{email}</strong>.
        </p>
        <p className="mt-1 text-sm text-muted">Tap the link in the email, or type the code here.</p>
      </div>
      <form
        className="mt-5 space-y-3"
        onSubmit={(e) => {
          e.preventDefault();
          if (code.length === 6) verify.mutate(code);
          else setError('The code is the 6 digits from the email.');
        }}
      >
        {error && <Alert tone="error">{error}</Alert>}
        <Field label="Code from the email">
          <Input
            value={code}
            onChange={(e) => change(e.target.value)}
            inputMode="numeric"
            autoComplete="one-time-code"
            maxLength={6}
            placeholder="123456"
            autoFocus
            className="text-center font-mono text-2xl tracking-[0.4em]"
          />
        </Field>
        <Button type="submit" className="w-full" loading={verify.isPending}>
          Sign in
        </Button>
      </form>
      <div className="mt-5 space-y-2 border-t border-line pt-4 text-center text-sm text-muted">
        <p>
          {resent ? 'Sent again. ' : 'Nothing there? Check your spam folder, or '}
          {wait > 0 ? (
            <span>send again in {wait} s.</span>
          ) : (
            <button
              type="button"
              className="font-medium text-brand hover:underline"
              onClick={() =>
                void onResend().then(
                  () => {
                    setResent(true);
                    setWait(RESEND_SECONDS);
                  },
                  (err: unknown) => setError(err instanceof ApiError ? err.message : String(err)),
                )
              }
            >
              send it again
            </button>
          )}
        </p>
        <p>
          <button type="button" className="font-medium text-brand hover:underline" onClick={onDifferentEmail}>
            Use a different email
          </button>
        </p>
        <p>IT accounts don’t get links or codes: use the IT staff tab and your password.</p>
      </div>
    </AuthCard>
  );
}

/** A password box with a Show/Hide button, for typing long passwords on a phone. */
function PasswordInput(props: InputHTMLAttributes<HTMLInputElement>) {
  const [shown, setShown] = useState(false);
  return (
    <div className="relative">
      <Input {...props} type={shown ? 'text' : 'password'} className="pr-16" />
      <button
        type="button"
        onClick={() => setShown((s) => !s)}
        aria-pressed={shown}
        className="absolute inset-y-0 right-0 flex items-center rounded-r-lg px-3 text-sm font-medium text-muted hover:text-text"
      >
        {shown ? 'Hide' : 'Show'}
      </button>
    </div>
  );
}

type LoginResult = { user: User } | { twoFactor: true; challenge: string };

function PasswordForm({ next, tabs }: { next: string | null; tabs: ReactNode }) {
  const [errors, setErrors] = useState<FieldErrors>({});
  const [challenge, setChallenge] = useState<string | null>(null);
  const setUser = useSetSessionUser();
  const navigate = useNavigate();
  const login = useMutation({
    mutationFn: (body: { login: string; password: string }) => api.post<LoginResult>('/api/auth/login', body),
    onSuccess: (result) => {
      try {
        localStorage.setItem(MODE_KEY, 'password');
      } catch {
        // Not remembered in private browsing.
      }
      if ('challenge' in result) return setChallenge(result.challenge);
      setUser(result.user);
      navigate(safePath(next) ?? '/', { replace: true });
    },
    onError: (err) => setErrors({ _: err instanceof ApiError ? err.message : String(err) }),
  });

  if (challenge) {
    return (
      <CodeStep
        challenge={challenge}
        next={next}
        onRestart={(message) => {
          setChallenge(null);
          setErrors({ _: message });
        }}
      />
    );
  }

  const submit = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const result = validate(loginSchema, formValues(e.currentTarget));
    if (!result.ok) return setErrors(result.fields);
    setErrors({});
    login.mutate(result.data);
  };

  return (
    <AuthCard title="IT staff sign in" subtitle="For technicians and administrators." top={tabs} back="/">
      <form onSubmit={submit} noValidate className="space-y-4">
        {errors._ && <Alert tone="error">{errors._}</Alert>}
        <Field label="Username or email" error={errors.login}>
          <Input name="login" autoComplete="username" autoCapitalize="none" autoFocus />
        </Field>
        <Field label="Password" error={errors.password}>
          <PasswordInput name="password" autoComplete="current-password" />
        </Field>
        <Button type="submit" className="w-full" loading={login.isPending}>
          <KeyRound className="size-4" aria-hidden />
          Sign in
        </Button>
      </form>
    </AuthCard>
  );
}

// ── Emailed link landing page ───────────────────────────────────────

export function VerifyPage() {
  const setUser = useSetSessionUser();
  const navigate = useNavigate();
  // Read before useHashToken clears the address bar.
  const [linkNext] = useState(() => new URLSearchParams(window.location.search).get('next'));
  const token = useHashToken();
  const [error, setError] = useState<string | null>(
    token
      ? null
      : 'This link is incomplete. Please copy the whole link from the email, or request a new one.',
  );
  const started = useRef(false);

  useEffect(() => {
    // StrictMode runs effects twice in development; the token is single-use.
    if (!token || started.current) return;
    started.current = true;
    api
      .post<{ user: User }>('/api/auth/magic-link/verify', { token })
      .then(({ user }) => {
        setUser(user);
        // Links in ticket emails say where to go; otherwise use what sign-in remembered.
        navigate(safePath(linkNext) ?? takeNext(), { replace: true });
      })
      .catch((err: unknown) =>
        setError(err instanceof ApiError ? err.message : 'Sign-in failed. Please try again.'),
      );
  }, [token, navigate, setUser, linkNext]);

  if (!error) return <Spinner label="Signing you in…" className="py-24" />;
  return (
    <AuthCard title="That link didn’t work">
      <Alert tone="error">{error}</Alert>
      <Link to="/sign-in" className="mt-5 inline-block font-medium text-brand hover:underline">
        Get a new sign-in link
      </Link>
    </AuthCard>
  );
}

/** Second step for IT accounts with two-step sign-in. */
function CodeStep({
  challenge,
  next,
  onRestart,
}: {
  challenge: string;
  next: string | null;
  onRestart: (message: string) => void;
}) {
  const setUser = useSetSessionUser();
  const navigate = useNavigate();
  const toast = useToast();
  const [recovery, setRecovery] = useState(false);
  const [code, setCode] = useState('');
  const [error, setError] = useState<string>();
  const verify = useMutation({
    mutationFn: () =>
      api.post<{ user: User; recoveryCodesLeft?: number }>('/api/auth/login/two-factor', { challenge, code }),
    onSuccess: ({ user, recoveryCodesLeft }) => {
      setUser(user);
      if (recoveryCodesLeft !== undefined && recoveryCodesLeft <= 3) {
        toast({
          tone: 'info',
          title: `${recoveryCodesLeft} recovery ${recoveryCodesLeft === 1 ? 'code' : 'codes'} left`,
          body: 'Make new ones in Profile & notifications, under two-step sign-in.',
        });
      }
      navigate(safePath(next) ?? '/', { replace: true });
    },
    onError: (err) => {
      const message = err instanceof ApiError ? err.message : String(err);
      // Timed out or too many tries: back to the password.
      if (/password again/.test(message)) return onRestart(message);
      setError(message);
      setCode('');
    },
  });

  return (
    <AuthCard
      title="Enter your code"
      subtitle={
        recovery
          ? 'Enter one of the recovery codes you saved when you set up two-step sign-in.'
          : 'Open your authenticator app and enter the 6-digit code for NABOCADO Helpdesk.'
      }
    >
      <form
        onSubmit={(e) => {
          e.preventDefault();
          if (code.trim()) verify.mutate();
        }}
        noValidate
        className="space-y-4"
      >
        {error && <Alert tone="error">{error}</Alert>}
        <Field label={recovery ? 'Recovery code' : 'Code'}>
          <Input
            key={String(recovery)}
            value={code}
            onChange={(e) => setCode(e.target.value)}
            inputMode={recovery ? 'text' : 'numeric'}
            autoComplete={recovery ? 'off' : 'one-time-code'}
            autoCapitalize="none"
            maxLength={20}
            autoFocus
            className="font-mono tracking-widest"
          />
        </Field>
        <Button type="submit" className="w-full" loading={verify.isPending}>
          <KeyRound className="size-4" aria-hidden />
          Sign in
        </Button>
      </form>
      <p className="mt-6 border-t border-line pt-5 text-center text-sm text-muted">
        <button
          type="button"
          onClick={() => {
            setRecovery((r) => !r);
            setCode('');
            setError(undefined);
          }}
          className="font-medium text-brand hover:underline"
        >
          {recovery ? 'Use the code from my app' : 'Lost your phone? Use a recovery code'}
        </button>
      </p>
    </AuthCard>
  );
}

// ── Confirming a guest's request ────────────────────────────────────

/**
 * The link in the "Please confirm your IT request" email. Confirming turns
 * the held request into a ticket, alerts IT, and signs the person in.
 */
export function ConfirmPage() {
  const setUser = useSetSessionUser();
  const navigate = useNavigate();
  const toast = useToast();
  const token = useHashToken();
  const [result, setResult] = useState<ConfirmedTicket | null>(null);
  const [error, setError] = useState<string | null>(
    token ? null : 'This link is incomplete. Please copy the whole link from the email.',
  );
  const started = useRef(false);

  useEffect(() => {
    // StrictMode runs effects twice in development; only the first click signs in.
    if (!token || started.current) return;
    started.current = true;
    api
      .post<ConfirmedTicket>('/api/ticket-requests/confirm', { token })
      .then((confirmed) => {
        if (!confirmed.user) return setResult(confirmed);
        setUser(confirmed.user);
        toast({
          tone: 'success',
          title: `Confirmed: ${confirmed.number}`,
          body: `IT has your request and will respond ${confirmed.estimatedResponse}.`,
        });
        navigate(`/tickets/${confirmed.number}`, { replace: true });
      })
      .catch((err: unknown) =>
        setError(err instanceof ApiError ? err.message : 'Confirming failed. Please try again.'),
      );
  }, [token, navigate, setUser, toast]);

  if (error) {
    return (
      <AuthCard title="That link didn’t work">
        <Alert tone="error">{error}</Alert>
        <Link to="/new" className="mt-5 inline-block font-medium text-brand hover:underline">
          Report the problem again
        </Link>
      </AuthCard>
    );
  }
  if (!result) return <Spinner label="Confirming your request…" className="py-24" />;
  return (
    <AuthCard
      title={result.alreadyConfirmed ? 'Already confirmed' : 'Request confirmed'}
      subtitle={
        <>
          Your ticket number is <strong className="font-mono text-text">{result.number}</strong>. IT will
          respond {result.estimatedResponse}.
        </>
      }
    >
      <div className="flex flex-col gap-2 sm:flex-row">
        <ButtonLink to={`/tickets/${result.number}`}>Open the ticket</ButtonLink>
        <ButtonLink to={`/status?number=${result.number}`} variant="secondary">
          Check status
        </ButtonLink>
      </div>
    </AuthCard>
  );
}

// ── First-run setup ─────────────────────────────────────────────────

export function SetupPage() {
  const { setupRequired, isLoading } = useSession();
  const setUser = useSetSessionUser();
  const navigate = useNavigate();
  const [errors, setErrors] = useState<FieldErrors>({});
  const setup = useMutation({
    mutationFn: (body: Record<string, string>) => api.post<{ user: User }>('/api/auth/setup', body),
    onSuccess: ({ user }) => {
      setUser(user);
      navigate('/', { replace: true });
    },
    onError: (err) =>
      setErrors(err instanceof ApiError ? { ...err.fields, _: err.message } : { _: String(err) }),
  });

  if (isLoading) return <Spinner />;
  if (!setupRequired) return <Navigate to="/sign-in" replace />;

  const submit = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const values = formValues(e.currentTarget);
    const result = validate(setupSchema, values);
    const fields: FieldErrors = result.ok ? {} : result.fields;
    if (values.password !== values.confirm) fields.confirm = 'Passwords do not match.';
    if (!result.ok || fields.confirm) return setErrors(fields);
    setErrors({});
    setup.mutate(result.data);
  };

  return (
    <AuthCard
      title="Set up the helpdesk"
      subtitle="Create the administrator account. This page closes once it’s done."
    >
      <form onSubmit={submit} noValidate className="space-y-4">
        {errors._ && <Alert tone="error">{errors._}</Alert>}
        <Field label="Your name" error={errors.name} required>
          <Input name="name" autoComplete="name" autoFocus />
        </Field>
        <Field label="Email" error={errors.email} required>
          <Input name="email" type="email" autoComplete="email" />
        </Field>
        <Field label="Username" error={errors.username} required>
          <Input name="username" autoComplete="username" autoCapitalize="none" />
        </Field>
        <Field
          label="Password"
          error={errors.password}
          hint="At least 10 characters, with upper- and lower-case letters and a number."
          required
        >
          <Input name="password" type="password" autoComplete="new-password" />
        </Field>
        <Field label="Confirm password" error={errors.confirm} required>
          <Input name="confirm" type="password" autoComplete="new-password" />
        </Field>
        <Button type="submit" className="w-full" loading={setup.isPending}>
          Create administrator
        </Button>
      </form>
    </AuthCard>
  );
}
